import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { BookOpen, ClipboardCheck } from "lucide-react";
import { ListingHeader } from "../../../components/listing-header";
import Loader from "../../../components/designs/loader";
import Modal from "../../../components/designs/modal";
import axiosService from "../../../core/services/axios-service";
import { ApiRoutes } from "../../../core/dictionary/api-url";
import { COURSE_REGISTRY } from "../../../core/dictionary/course-registry";

export default function CoachTeamMemberDashboard() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [courses, setCourses] = useState([]);
  // Course pending the "ready to start?" confirmation (progress === 0).
  const [confirmCourse, setConfirmCourse] = useState(null);
  const [starting, setStarting] = useState(false);
  const [confirmError, setConfirmError] = useState("");
  // Course the user tapped that is already 100% complete.
  const [completedCourse, setCompletedCourse] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchCourses = async () => {
      setLoading(true);
      setError("");
      try {
        const res = await axiosService.get(ApiRoutes.ASSIGNED_COURSES_BY_USER);
        const mapped = (res?.data?.data || []).map((item) => ({
          _id: item.course._id,
          name: item.course.courseName,
          thumbnail: item.course.imageUrl,
          progress: item.coursePercentage ?? 0,
        }));
        setCourses(mapped);
      } catch (err) {
        setError("Failed to load courses. Please try again.");
      } finally {
        setLoading(false);
      }
    };
    fetchCourses();
  }, []);

  // Open a course: progress 0 → ask the user to confirm a fresh start;
  // otherwise resume at the module derived from coursePercentage.
  const handleCourseOpen = (course) => {
    const progress = Math.min(100, Math.max(0, course.progress ?? 0));
    if (progress >= 100) {
      // Completed courses can't be reopened — let the user know.
      setCompletedCourse(course);
      return;
    }
    if (progress === 0) {
      setConfirmError("");
      setConfirmCourse(course);
      return;
    }
    // Each module is worth 20%, so floor(progress / 20) is the 0-based index of
    // the module the user is currently working through (divisor + 1 = module #).
    const moduleIndex = Math.floor(progress / 20);
    navigate(`/course/${course._id}`, { state: { startModuleIndex: moduleIndex } });
  };

  // Confirm a fresh start: register progress with the backend, then open the course.
  const handleConfirmStart = async () => {
    if (!confirmCourse) return;
    setStarting(true);
    setConfirmError("");
    try {
      const firstModule = COURSE_REGISTRY[confirmCourse._id]?.modules?.[0];
      await axiosService.post(ApiRoutes.COURSE_PROGRESS, {
        courseId: confirmCourse._id,
        coursePercentage: 0,
        currentPage: "1",
        start_time: new Date().toISOString(),
        module_name: firstModule?.title ?? "",
      });
      const courseId = confirmCourse._id;
      setConfirmCourse(null);
      navigate(`/course/${courseId}`, { state: { startModuleIndex: 0 } });
    } catch (err) {
      setConfirmError("Failed to start the course. Please try again.");
    } finally {
      setStarting(false);
    }
  };

  return (
    <>
      <ListingHeader
        title="My Courses"
        breadCrumb={[{ label: "Dashboard", href: "/dashboard" }]}
        actions={[
          {
            label: "Submit Drill",
            className: "btn btn-rescuern-primary",
            icon: ClipboardCheck,
            onClick: () => navigate("/submit-drill"),
          },
        ]}
        dropdown={null}
      />

      {loading ? (
        <div className="course-loader-wrapper">
          <Loader text="Loading your courses..." />
        </div>
      ) : error ? (
        <div className="course-dashboard-section">
          <p className="text-danger">{error}</p>
        </div>
      ) : courses.length === 0 ? (
        <div className="course-dashboard-empty">
          <div className="course-dashboard-empty__icon">
            <BookOpen size={48} strokeWidth={1.5} />
          </div>
          <p className="course-dashboard-empty__title">No courses assigned yet</p>
          <p className="course-dashboard-empty__sub">
            Your assigned courses will appear here once they are added.
          </p>
        </div>
      ) : (
        <div className="course-dashboard-section">
          <div className="course-grid">
            {courses.map((course) => {
              const progress = Math.min(100, Math.max(0, course.progress ?? 0));
              return (
                <div
                  key={course._id}
                  className="course-card"
                  onClick={() => handleCourseOpen(course)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => e.key === "Enter" && handleCourseOpen(course)}
                >
                  {course.thumbnail ? (
                    <img
                      src={course.thumbnail}
                      alt={course.name}
                      className="course-card__thumbnail"
                    />
                  ) : (
                    <div className="course-card__thumbnail-placeholder">
                      <BookOpen size={40} strokeWidth={1.2} />
                    </div>
                  )}

                  <div className="course-card__body">
                    <h3 className="course-card__name">{course.name}</h3>

                    {progress === 100 ? (
                      <span className="course-card__completed-badge">
                        ✓ Completed
                      </span>
                    ) : (
                      <>
                        <div className="course-card__progress-label">
                          <span>Course Progress</span>
                          <strong>{progress}%</strong>
                        </div>
                        <div className="course-card__progress-bar">
                          <div
                            className="course-card__progress-fill"
                            style={{ "--progress-width": `${progress}%` }}
                          />
                        </div>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <Modal
        isOpen={!!confirmCourse}
        title="Start this course?"
        onClose={() => !starting && setConfirmCourse(null)}
        onConfirm={handleConfirmStart}
        confirmLabel={starting ? "Starting…" : "Yes, start"}
        confirmDisabled={starting}
      >
        <p style={{ margin: 0 }}>
          Are you ready to start{" "}
          <strong>{confirmCourse?.name}</strong>? Your progress will be tracked
          from here.
        </p>
        {confirmError && (
          <p className="text-danger" style={{ marginTop: 12, marginBottom: 0 }}>
            {confirmError}
          </p>
        )}
      </Modal>

      <Modal
        isOpen={!!completedCourse}
        title="Course already completed"
        onClose={() => setCompletedCourse(null)}
      >
        <p style={{ margin: 0 }}>
          You’ve already completed{" "}
          <strong>{completedCourse?.name}</strong>, so it can’t be opened again.
        </p>
      </Modal>
    </>
  );
}
